import { z } from 'zod';

/**
 * Wave 21 — Fornecedor 360 (Supplier scorecard).
 *
 * Per-supplier drill-in that aggregates purchase orders and payments
 * across every project the caller can see. Backs the `/fornecedores/:id`
 * page — the procurement equivalent of the Wave 17 Carteira aggregate.
 *
 * Metrics we compute server-side so the UI stays thin:
 *   - totalCommittedCents  — sum of po.total_cents for issued/received/partial
 *   - totalPaidCents       — sum of settled payments linked to this supplier
 *   - outstandingCents     — committed − paid (floor at 0)
 *   - activePOsCount       — status ∈ issued/partially_received
 *   - completedPOsCount    — status = received
 *   - cancelledPOsCount    — status = cancelled
 *   - onTimePercent        — % of received POs where received_at ≤ expected_delivery_at
 *   - lastActivityAt       — most recent po.updated_at / payment.occurred_at
 *
 * Scoping: a caller sees a supplier's rollup only across the projects
 * they have access to (project_members inner join). Admin sees all.
 * Financial numbers redact the same way as Wave 17 when the caller is
 * client/broker-only.
 */

export const supplierProjectBreakdownSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  /** Total committed to this supplier for this project (cents, BRL). */
  committedCents: z.number().int().nonnegative(),
  /** Total settled to this supplier for this project. */
  paidCents: z.number().int().nonnegative(),
  /** `committedCents - paidCents`, floored at 0. */
  outstandingCents: z.number().int().nonnegative(),
  /** POs currently active on this project for this supplier. */
  activePOsCount: z.number().int().nonnegative(),
});
export type SupplierProjectBreakdown = z.infer<
  typeof supplierProjectBreakdownSchema
>;

export const supplierRecentPoSchema = z.object({
  id: z.string(),
  code: z.string(),
  title: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  status: z.string(),
  totalCents: z.number().int().nonnegative(),
  issuedAt: z.string().nullable(),
  expectedDeliveryAt: z.string().nullable(),
  receivedAt: z.string().nullable(),
  /**
   * Derived: true when received_at ≤ expected_delivery_at. Null when one
   * of the dates is missing or the PO hasn't been received yet.
   */
  deliveredOnTime: z.boolean().nullable(),
});
export type SupplierRecentPo = z.infer<typeof supplierRecentPoSchema>;

export const supplierScorecardMetricsSchema = z.object({
  totalCommittedCents: z.number().int().nonnegative(),
  totalPaidCents: z.number().int().nonnegative(),
  outstandingCents: z.number().int().nonnegative(),
  activePOsCount: z.number().int().nonnegative(),
  completedPOsCount: z.number().int().nonnegative(),
  cancelledPOsCount: z.number().int().nonnegative(),
  /** 0..100 rounded integer; null when no received POs with both dates. */
  onTimePercent: z.number().int().min(0).max(100).nullable(),
  /** Last supplier-relevant touch (ISO string). Null when supplier has no activity. */
  lastActivityAt: z.string().nullable(),
});
export type SupplierScorecardMetrics = z.infer<
  typeof supplierScorecardMetricsSchema
>;

export const supplierScorecardSchema = z.object({
  supplierId: z.string(),
  legalName: z.string(),
  displayName: z.string().nullable(),
  cnpj: z.string().nullable(),
  archivedAt: z.string().nullable(),
  metrics: supplierScorecardMetricsSchema,
  byProject: z.array(supplierProjectBreakdownSchema),
  recentPOs: z.array(supplierRecentPoSchema),
  /** Same semantics as Wave 17 — true when the caller only holds read-only roles. */
  financialsRedacted: z.boolean(),
  generatedAt: z.string(),
  /**
   * External enrichment (Wave 100). All optional — older suppliers may
   * have null fields until the cron sweep fills them in.
   */
  cnpjStatus: z.string().nullable().optional(),
  cnpjActivity: z.string().nullable().optional(),
  cnpjAddress: z.string().nullable().optional(),
  /** JSON-serialized array of {nome, qualificacao}. */
  cnpjPartners: z.string().nullable().optional(),
  cnpjEnrichedAt: z.string().nullable().optional(),
  isSanctioned: z.boolean().optional(),
  /** JSON-serialized SanctionsHit[]. */
  sanctionsDetail: z.string().nullable().optional(),
  sanctionsCheckedAt: z.string().nullable().optional(),
});
export type SupplierScorecard = z.infer<typeof supplierScorecardSchema>;
